import React, { useEffect, useState } from "react";
import {
  FileText,
  Presentation,
  FileArchive,
  FileType2,
  Image as ImageIcon,
  Eye,
  Download,
  FolderOpen,
  Play,
  Clock,
} from "lucide-react";
import apiClient from "../../../utils/apiClient";
import { AppBadge } from "../../ui";
import useMaterialViewer, { isViewableType } from "../../Materials/useMaterialViewer";
import AccessBadge from "../../ContentProtection/AccessBadge";
import MaterialVideoModal from "../../Materials/MaterialVideoModal";
import { canDownloadFile } from "../../ContentProtection/contentAccess";
import { fmtDate, PanelLoading, PanelEmpty } from "./panelShared";

const VIDEO_TYPES = ["MP4", "MOV", "WEBM", "MKV"];
const isVideoMaterial = (m) =>
  m?.isVideo || VIDEO_TYPES.includes(String(m?.fileType || "").toUpperCase());

const fmtDuration = (min) => {
  if (!min || min <= 0) return "";
  if (min >= 60) return `${Math.floor(min / 60)}h ${Math.round(min % 60)}m`;
  return `${Math.round(min)}m`;
};

/**
 * MaterialsPanel — study materials shared with the selected batch + course.
 * Data: GET /materials/batch/:batchId  →  { data: { batchMaterials, courseMaterials } }
 * View: GET /materials/:id/view (useMaterialViewer) — the URL plus what the
 * student may do with the file; a view-only file has no download.
 */

const fmtBytes = (bytes) => {
  if (!bytes || bytes <= 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
};

// One hue per FILE TYPE — a category map, so the chart ramp. `token` rather than
// a colour because the icon tint was built by concatenating an alpha onto a hex
// (`${color}1a`), which only works on a literal.
const typeMeta = (fileType = "") => {
  const t = String(fileType).toLowerCase();
  if (t.includes("pdf")) return { Icon: FileText, token: "--chart-4", label: "PDF", viewable: true };
  if (t.includes("ppt") || t.includes("presentation"))
    return { Icon: Presentation, token: "--chart-3", label: "PPT", viewable: false };
  if (t.includes("zip") || t.includes("rar") || t.includes("archive") || t.includes("7z"))
    return { Icon: FileArchive, token: "--chart-1", label: "ZIP", viewable: false };
  if (t.includes("doc")) return { Icon: FileText, token: "--chart-5", label: "DOC", viewable: false };
  if (/png|jpe?g|gif|webp|image|svg/.test(t))
    return { Icon: ImageIcon, token: "--chart-2", label: "IMG", viewable: true };
  return { Icon: FileType2, token: "--muted-foreground", label: "FILE", viewable: false };
};

const MaterialCard = ({ mat, onView, onDownload }) => {
  const { Icon, token, label } = typeMeta(mat.fileType);
  // Render docs (pdf/docx/ppt/xls/img/text) inside the LMS; only true non-viewable
  // types (archives, etc.) fall back to a download — unless the file is
  // view-only, which always opens in the viewer.
  const viewable = isViewableType(mat.fileType) || !canDownloadFile(mat);

  const size = fmtBytes(mat.fileSize);
  const date = fmtDate(mat.sessionDate || mat.createdAt);

  return (
    <article className="ml-matcard">
      <div className="ml-matcard__top">
        <span
          className="ml-matcard__icon"
          style={{ background: `hsl(var(${token}) / 0.10)`, color: `hsl(var(${token}))` }}
        >
          <Icon size={22} />
        </span>
        <span className="ml-matcard__badge">{label}</span>
      </div>
      <h4 className="ml-matcard__title" title={mat.title}>
        {mat.title}
      </h4>
      {(date || size) && (
        <p className="ml-matcard__meta">
          {date}
          {date && size ? " · " : ""}
          {size}
        </p>
      )}
      <AccessBadge access={mat.access} className="ml-matcard__access" />
      <button
        type="button"
        className="ml-matcard__btn"
        onClick={() => (viewable ? onView(mat) : onDownload(mat))}
      >
        {viewable ? (
          <>
            <Eye size={15} /> View
          </>
        ) : (
          <>
            <Download size={15} /> Download
          </>
        )}
      </button>
    </article>
  );
};

const VideoCard = ({ mat, onPlay }) => {
  const size = fmtBytes(mat.fileSize);
  const duration = fmtDuration(mat.duration);
  return (
    <article className="ml-matcard">
      <div className="ml-matcard__top">
        <span
          className="ml-matcard__icon"
          style={{ background: "hsl(var(--chart-5) / 0.10)", color: "hsl(var(--chart-5))" }}
        >
          <Play size={22} />
        </span>
        {mat.isPreview ? (
          <AppBadge color="emerald" size="sm">Free Preview</AppBadge>
        ) : (
          <span className="ml-matcard__badge">VIDEO</span>
        )}
      </div>
      <h4 className="ml-matcard__title" title={mat.title}>
        {mat.title}
      </h4>
      {(duration || size) && (
        <p className="ml-matcard__meta">
          {duration && (
            <>
              <Clock size={12} /> {duration}
            </>
          )}
          {duration && size ? " · " : ""}
          {size}
        </p>
      )}
      <button type="button" className="ml-matcard__btn" onClick={() => onPlay(mat)}>
        <Play size={15} /> Play
      </button>
    </article>
  );
};

const MaterialsPanel = ({ batchId }) => {
  const [loading, setLoading] = useState(true);
  const [batchMats, setBatchMats] = useState([]);
  const [courseMats, setCourseMats] = useState([]);
  const [error, setError] = useState("");
  const [playingVideo, setPlayingVideo] = useState(null);
  const { openMaterial, downloadMaterial, viewer } = useMaterialViewer();
  const handlePlayVideo = (mat) => { if (mat?._id) setPlayingVideo(mat); };

  useEffect(() => {
    if (!batchId) {
      setLoading(false);
      return undefined;
    }
    let alive = true;
    setLoading(true);
    setError("");
    apiClient
      .get(`/materials/batch/${batchId}`, { params: { page: 1, limit: 100 } })
      .then(({ data }) => {
        if (!alive) return;
        const d = data?.data;
        setBatchMats(d?.batchMaterials || (Array.isArray(d) ? d : []));
        setCourseMats(d?.courseMaterials || []);
      })
      .catch(() => {
        if (alive) setError("Failed to load materials.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [batchId]);

  if (loading) return <PanelLoading />;
  if (!batchId) return <PanelEmpty icon={FolderOpen}>You are not enrolled in any batch yet.</PanelEmpty>;
  if (error) return <PanelEmpty icon={FolderOpen}>{error}</PanelEmpty>;

  const courseVideos = courseMats.filter(isVideoMaterial);
  const courseDocs = courseMats.filter((m) => !isVideoMaterial(m));

  if (!batchMats.length && !courseDocs.length && !courseVideos.length)
    return <PanelEmpty icon={FolderOpen}>Materials shared by your instructor will appear here.</PanelEmpty>;

  return (
    <div className="ml-sections">
      {courseVideos.length > 0 && (
        <section className="ml-section">
          <h3 className="ml-section__title">Course Videos</h3>
          <div className="ml-matgrid">
            {courseVideos.map((m) => (
              <VideoCard key={m._id} mat={m} onPlay={handlePlayVideo} />
            ))}
          </div>
        </section>
      )}
      {batchMats.length > 0 && (
        <section className="ml-section">
          <h3 className="ml-section__title">Class Materials</h3>
          <div className="ml-matgrid">
            {batchMats.map((m) => (
              <MaterialCard key={m._id} mat={m} onView={openMaterial} onDownload={downloadMaterial} />
            ))}
          </div>
        </section>
      )}
      {courseDocs.length > 0 && (
        <section className="ml-section">
          <h3 className="ml-section__title">Course Materials</h3>
          <div className="ml-matgrid">
            {courseDocs.map((m) => (
              <MaterialCard key={m._id} mat={m} onView={openMaterial} onDownload={downloadMaterial} />
            ))}
          </div>
        </section>
      )}
      {viewer}

      {/* Course video player */}
      <MaterialVideoModal material={playingVideo} onClose={() => setPlayingVideo(null)} />
    </div>
  );
};

export default MaterialsPanel;
